/**
 * ParkFinderService
 *
 * Finds nearby parks using OpenStreetMap's Overpass API
 *
 * Searches for:
 * - Parks (leisure=park)
 * - Recreation grounds (landuse=recreation_ground)
 * - Gardens (leisure=garden)
 * - Beaches (natural=beach)
 *
 * Playgrounds are deliberately excluded: we don't send people to drink
 * where kids play. A park's own `playground=yes` tag is still shown as a feature.
 */

import { buildAroundQuery, runOverpassQuery, toVenuesByDistance } from './overpass';
import { formatAddress } from '../utils/geo';

const SEARCH_RADIUS = 3000; // Search within 3km radius (parks can be further)
const PARK_FILTERS = [
  ['leisure', 'park'],
  ['landuse', 'recreation_ground'],
  ['leisure', 'garden'],
  ['natural', 'beach'],
];

/**
 * Find nearby parks within a radius
 * @param {number} latitude - User's latitude
 * @param {number} longitude - User's longitude
 * @param {number} radius - Search radius in meters (default: 3000m = 3km)
 * @returns {Promise<Array>} Parks sorted by distance (empty if none nearby)
 * @throws {VenueLookupError} When the Overpass API can't be reached
 */
export const findNearbyParks = async (latitude, longitude, radius = SEARCH_RADIUS) => {
  const query = buildAroundQuery(PARK_FILTERS, radius, latitude, longitude);
  const data = await runOverpassQuery(query);

  return toVenuesByDistance(data.elements, latitude, longitude, (tags) => ({
    name: tags.name || getDefaultName(tags),
    type: tags.leisure || tags.landuse || tags.natural || 'park',
    address: formatAddress(tags),
    description: tags.description,
    website: tags.website,
    hasPlayground: tags.playground === 'yes',
    hasBBQ: tags.bbq === 'yes',
    hasSports: tags.sport !== undefined,
  }));
};

/**
 * Get default name based on park type
 */
const getDefaultName = (tags) => {
  if (tags.natural === 'beach') return 'Beach';
  if (tags.leisure === 'garden') return 'Garden';
  return 'Local Park';
};

/**
 * Get the closest park from a list
 */
export const getClosestPark = (parks) => {
  if (!parks || parks.length === 0) return null;
  return parks[0]; // Already sorted by distance
};
